import { ComponentRef, Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, Type, ViewContainerRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';

/**
 * MDI 탭 하나의 화면 컴포넌트를 생성하고, 활성 여부에 따라 변경 감지를 붙였다 뗀다.
 * <p>
 * - 생성: ViewContainerRef.createComponent + setInput
 * - 비활성: changeDetectorRef.detach() → 부모 변경 감지에서 제외 (DOM과 상태는 그대로 유지)
 * - 활성: changeDetectorRef.reattach() → 다시 변경 감지 대상
 * - 제거: 디렉티브가 없어질 때(탭 닫힘) 컴포넌트 destroy
 * - 닫기 요청: 화면의 embedClose는 npMdiTabOutletClose로, embedDismiss는 npMdiTabOutletDismiss로 전달 (닫을지는 호스트가 정한다)
 * <p>
 * 비활성 상태로 생성된 탭(한번에 여러 개 열기 등)은 한 번도 변경 감지가 돌지 않았으므로
 * 처음 활성화될 때 ngOnInit부터 초기화된다.
 *
 * <pre>
 * &lt;ng-container [npMdiTabOutlet]="component" [npMdiTabOutletInputs]="inputs" [npMdiTabOutletActive]="active"
 *     (npMdiTabOutletClose)="close()" (npMdiTabOutletDismiss)="dismiss()" /&gt;
 * </pre>
 */
@Directive({
  selector: '[npMdiTabOutlet]',
  standalone: true,
})
export class NpMdiTabOutletDirective implements OnChanges, OnDestroy {

  @Input({ required: true }) npMdiTabOutlet!: Type<any>;
  @Input() npMdiTabOutletInputs?: Record<string, any>;
  @Input() npMdiTabOutletActive: boolean = false;

  /** 이 화면이 속한 탭 id (MDI가 탭별 화면을 찾을 때 쓴다) */
  @Input() npMdiTabOutletTabId?: number;

  /** 화면이 닫기를 요청할 때 (화면의 embedClose) */
  @Output() npMdiTabOutletClose = new EventEmitter<any>();

  /** 화면이 취소(닫기)를 요청할 때 (화면의 embedDismiss) */
  @Output() npMdiTabOutletDismiss = new EventEmitter<any>();

  private vcr = inject(ViewContainerRef);
  private componentRef?: ComponentRef<any>;
  private closeSubscriptions: Subscription[] = [];

  ngOnChanges() {
    if (!this.componentRef) {
      this.componentRef = this.vcr.createComponent(this.npMdiTabOutlet);
      Object.entries(this.npMdiTabOutletInputs ?? {}).forEach(([name, value]) => this.componentRef!.setInput(name, value));

      this.forward('embedClose', this.npMdiTabOutletClose);
      this.forward('embedDismiss', this.npMdiTabOutletDismiss);
    }

    if (this.npMdiTabOutletActive) {
      this.componentRef.changeDetectorRef.reattach();
    } else {
      this.componentRef.changeDetectorRef.detach();
    }
  }

  /** 생성한 화면 컴포넌트 (없으면 undefined) */
  get instance(): any {
    return this.componentRef?.instance;
  }

  /** 화면에 해당 이벤트가 있으면 호스트에 전달한다. */
  private forward(name: string, target: EventEmitter<any>) {
    const output = this.componentRef!.instance[name];
    if (output instanceof EventEmitter) {
      this.closeSubscriptions.push(output.subscribe((value: any) => target.emit(value)));
    }
  }

  ngOnDestroy() {
    this.closeSubscriptions.forEach(s => s.unsubscribe());
    this.componentRef?.destroy();
  }

}
